import React, { useState, useEffect, useRef } from "react";
import { ILinkedWizardListItem } from "interfaces/Components/LinkedWizard/ILinkedWizardListItem";
import { Button, ListGroup } from "react-bootstrap";
import { Error } from "../Error/Error";
import { PlusSquareFill } from "react-bootstrap-icons";
import { LinkedWizardListItem } from "./LinkedWizardListItem";
import "./LinkedWizard.scss"

interface ILinkedWizardListProps {
    uniqueName: string;
    items: Array<ILinkedWizardListItem>;
    onChange: (uniqueName : string, key : string | undefined) => void;
    onTargetReferenceChange: (uniqueName :string, elementReference : HTMLButtonElement | null) => void;
    onOverlayClicked: (uniqueName : string) => void;
    onListShift : (shift : number, uniqueName : string) => void;
    title: string;
    disabled?: boolean;
    overlayText: string;
    overlayShown: boolean;
    maxHeight?: number;
    validationMessage?: string;
}


const LinkedWizardList = ({ uniqueName, items, onChange, title, disabled, overlayShown, overlayText, onOverlayClicked, maxHeight, validationMessage, onListShift, onTargetReferenceChange } : ILinkedWizardListProps) => {

    const [listItems, setListItems] = useState<Array<ILinkedWizardListItem>>([]);

    const selectedItemLabel = listItems.find(item => item.selected)?.label ?? "<None>";

    const firstItemReference = useRef<HTMLButtonElement | null>(null);

    useEffect(() => {
        onTargetReferenceChange(uniqueName, firstItemReference.current);
    }, [firstItemReference.current, firstItemReference])

    useEffect(() => {
            let tempItems : Array<ILinkedWizardListItem> = [...listItems];
            
            if (tempItems.length === 0 && items.length !== 0) {
                items.forEach(item => {
                    tempItems.push({...item});
                });
            }
        
            tempItems.forEach((item, index) => {
                item.disabled = (disabled || overlayShown) ? true : items[index].disabled;
            });
    
            setListItems([...tempItems]);

    }, [disabled, overlayShown, items]);

    const onSelect = (key: string) => {
        
        let tempItems : Array<ILinkedWizardListItem> = [...listItems];
        let selectedItemKey : string | undefined = key;

        tempItems.forEach((item) => {
            if (item.key === key) {
                item.selected = !item.selected;
                selectedItemKey = item.selected ? item.key : undefined;
            }
            else
            {
                item.selected = false;
            }
            
        });

        setListItems([...tempItems]);

        onChange(uniqueName, selectedItemKey);
    };

    const onKeyDown = ( e : React.KeyboardEvent<HTMLElement>) => {
        if (e.code === "ArrowLeft" || e.code === "ArrowRight") {
            e.preventDefault();
            const shift : number = (e.code === "ArrowLeft") ? -1 : 1;
            onListShift(shift, uniqueName);
        }
    };

    let wrapperStyle = {};
    
    if (maxHeight)
    {
        wrapperStyle = { 'maxHeight': `${maxHeight}px` };
    }

    return <>
        {validationMessage && <Error message={validationMessage} />}
        <div className="linked-wizard-list-holder">
            {overlayShown && 
                <Button onClick={() => onOverlayClicked(uniqueName)} onKeyDown={onKeyDown} variant="secondary" className="overlay-button" aria-labelledby={`${uniqueName}-label`}>
                </Button>
            }
            {overlayShown && 
                <span id={`${uniqueName}-label`} className="overlay-button-label fs-5" onClick={() => onOverlayClicked(uniqueName)}>
                    <PlusSquareFill size={42} className="text-primary mb-2 overlay-icon" />
                    <br />
                    {overlayText}
                </span>
            }
            <ListGroup>
                <ListGroup.Item className="linked-wizard-list-header fs-5 text-white">{title}</ListGroup.Item>
                <ListGroup.Item data-testid="linked-wizard-selected-item" className="whitespace-bg-info whitespace-black-text fst-italic"><span className="visually-hidden">Current value:&nbsp;</span>{selectedItemLabel}</ListGroup.Item>
                <div className="linked-wizard-list-wrapper" style={wrapperStyle}>
                    {listItems.map((item, index) => {
                        if (index === 0) {
                            return <LinkedWizardListItem ref={firstItemReference} key={item.key} item={item} onClick={onSelect} onKeyDown={onKeyDown} />
                        } else {
                            return <LinkedWizardListItem key={item.key} item={item} onClick={onSelect} onKeyDown={onKeyDown}/>
                        }
                    })}
                </div>
            </ListGroup>
        </div>
    </>
};

export { LinkedWizardList }